import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CopyButton from "@/components/CopyButton";
import api from "@/lib/api";
import {
  Home, GraduationCap, CalendarDays, FolderOpen, Calculator, MessageSquareText,
  Phone, LifeBuoy, LogOut, Menu, X, Gift, ShieldCheck, KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/portal", label: "Start Here", icon: Home, end: true },
  { to: "/portal/training", label: "The Training", icon: GraduationCap },
  { to: "/portal/weekend-plan", label: "The Weekend Plan", icon: CalendarDays },
  { to: "/portal/assets", label: "Asset Vault", icon: FolderOpen },
  { to: "/portal/demo-offer", label: "Demo Offer", icon: Calculator },
  { to: "/portal/scripts", label: "Scripts", icon: MessageSquareText },
  { to: "/portal/book-call", label: "Book Your Launch Call", icon: Phone },
  { to: "/portal/support", label: "Support", icon: LifeBuoy },
];

function SidebarContent({ onNavigate }) {
  const { user, logout, progress } = useAuth();
  const [referral, setReferral] = useState(null);
  const nav = useNavigate();

  React.useEffect(() => { api.get("/referral").then((r) => setReferral(r.data)).catch(() => {}); }, []);

  const refLink = referral ? `${window.location.origin}/?ref=${referral.referral_code}` : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", color: "#E9ECE7" }}>
      <div style={{ padding: "22px 22px 14px" }}>
        <span className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 17, letterSpacing: "-0.02em", color: "#fff" }}>
          Weekend AI Offer
        </span>
      </div>

      <nav style={{ flex: 1, padding: "8px 12px", overflowY: "auto" }}>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to} to={to} end={end} onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            style={({ isActive }) => ({
              display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
              borderRadius: 10, marginBottom: 4, textDecoration: "none",
              fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 500,
              color: isActive ? "#0F1317" : "#aeb6bd",
              background: isActive ? "#FFDF57" : "transparent",
            })}
          >
            <Icon size={18} /> {label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/portal/admin" onClick={onNavigate} data-testid="nav-admin"
            style={({ isActive }) => ({
              display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
              borderRadius: 10, marginTop: 8, textDecoration: "none", fontSize: 15, fontWeight: 500,
              color: isActive ? "#0F1317" : "#aeb6bd", background: isActive ? "#FFDF57" : "transparent",
            })}>
            <ShieldCheck size={18} /> Admin
          </NavLink>
        )}
      </nav>

      {/* Referral block */}
      <div style={{ margin: "12px", padding: 14, background: "#151b21", borderRadius: 12, border: "1px solid #2a333c" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#FFDF57" }}>
          <Gift size={16} /> <span className="font-mono-plex" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>One referral covers your $97</span>
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <input readOnly value={refLink} data-testid="referral-link" style={{
            flex: 1, minWidth: 0, background: "#0F1317", border: "1px solid #2a333c", color: "#aeb6bd",
            borderRadius: 8, padding: "7px 9px", fontSize: 11, fontFamily: "IBM Plex Mono, monospace",
          }} />
          <CopyButton text={refLink} label="" testId="referral-copy" />
        </div>
        <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="font-archivo" data-testid="referral-count" style={{ fontWeight: 900, fontSize: 22, color: "#fff" }}>{referral ? referral.referrals : 0}</span>
          <span className="font-mono-plex" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "#aeb6bd" }}>Your referrals</span>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #2a333c", padding: 16 }}>
        <div style={{ fontSize: 13, color: "#aeb6bd" }} data-testid="member-name">{user?.email}</div>
        <button onClick={() => nav("/change-password")} data-testid="change-password-link" style={{
          marginTop: 10, display: "flex", alignItems: "center", gap: 8, background: "transparent",
          border: "1px solid #2a333c", color: "#E9ECE7", borderRadius: 8, padding: "8px 12px", cursor: "pointer",
          fontFamily: "Archivo, sans-serif", fontWeight: 700, textTransform: "uppercase", fontSize: 12, width: "100%",
        }}>
          <KeyRound size={15} /> Change password
        </button>
        <button onClick={() => { logout(); nav("/"); }} data-testid="logout-button" style={{
          marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "transparent",
          border: "1px solid #2a333c", color: "#E9ECE7", borderRadius: 8, padding: "8px 12px", cursor: "pointer",
          fontFamily: "Archivo, sans-serif", fontWeight: 700, textTransform: "uppercase", fontSize: 12, width: "100%",
        }}>
          <LogOut size={15} /> Log out
        </button>
      </div>
    </div>
  );
}

export default function PortalLayout() {
  const { progress } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const percent = progress?.percent ?? 0;

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "#F5F6F3" }}>
      {/* Desktop sidebar */}
      <aside style={{ width: 276, background: "#0F1317", position: "sticky", top: 0, height: "100vh", flexShrink: 0 }} className="portal-sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
          <div onClick={() => setMobileOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 276, background: "#0F1317" }}>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" style={{ position: "absolute", right: 12, top: 12, background: "transparent", border: "none", color: "#fff", zIndex: 2, cursor: "pointer" }}><X /></button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {/* Mobile top bar */}
        <div className="portal-topbar-mobile" style={{ display: "none", position: "sticky", top: 0, zIndex: 40, background: "#0F1317", color: "#fff", padding: "12px 16px", alignItems: "center", justifyContent: "space-between" }}>
          <span className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 15 }}>Weekend AI Offer</span>
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}><Menu /></button>
        </div>

        {/* Progress bar */}
        <div style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(245,246,243,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid var(--line)", padding: "14px 24px" }}>
          <div style={{ maxWidth: 860, margin: "0 auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span className="font-mono-plex" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)" }} data-testid="progress-label">
                You are {percent}% through launch weekend
              </span>
            </div>
            <div style={{ height: 8, background: "#e5e8e2", borderRadius: 99, overflow: "hidden" }}>
              <div data-testid="progress-fill" style={{ width: `${percent}%`, height: "100%", background: percent >= 100 ? "#FFDF57" : "#2038E6", transition: "width 0.4s ease" }} />
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 24px 80px" }} className="fade-up">
          <Outlet />
        </div>
      </main>

      <style>{`
        @media (max-width: 900px) {
          .portal-sidebar-desktop { display: none; }
          .portal-topbar-mobile { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
